import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { apiUrl } from "../../utils/backendUrl";
import { socket } from "../../routes/IsLoggedIn";
import { useMiniChat, miniChatBus } from "../../context/MiniChatContext";
import styles from "./MiniChat.module.css";

// ─── Helpers ─────────────────────────────────────────────────────────

function friendlyTimeShort(isoDate) {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Math.max(0, Date.now() - d.getTime());
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  return `${days}d`;
}

function fileIcon(type) {
  if (type.startsWith("image/")) return "fa-image";
  if (type.startsWith("video/")) return "fa-film";
  if (type.startsWith("audio/")) return "fa-music";
  return "fa-file";
}

// ─── Component ───────────────────────────────────────────────────────

export default function MiniChat({ chat, rightPosition }) {
  const { currentUser, closeMiniChat, toggleMinimize, setConversationId } =
    useMiniChat();

  const { id: chatId, user: friend, conversationId, minimized } = chat;

  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const friendName =
    `${friend.first_name || ""} ${friend.last_name || ""}`.trim() ||
    friend.username ||
    "User";

  // ─── File management helpers ──────────────────────────────────────

  const addFiles = useCallback((files) => {
    const fileArray = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...fileArray].slice(0, 5));

    fileArray.forEach((file) => {
      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setFilePreviews((prev) => [...prev, { name: file.name, url, type: file.type }]);
      } else {
        setFilePreviews((prev) => [
          ...prev,
          { name: file.name, url: null, type: file.type, size: file.size },
        ]);
      }
    });
  }, []);

  const removeFile = useCallback((index) => {
    setSelectedFiles((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
    setFilePreviews((prev) => {
      const updated = [...prev];
      if (updated[index]?.url) URL.revokeObjectURL(updated[index].url);
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  const clearFiles = useCallback(() => {
    filePreviews.forEach((p) => { if (p.url) URL.revokeObjectURL(p.url); });
    setSelectedFiles([]);
    setFilePreviews([]);
  }, [filePreviews]);

  // ─── 1. Resolve conversation + load messages ─────────────────────

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        setLoading(true);
        setError("");

        const { data: convData } = await axios.post(
          apiUrl("/api/v1/messages/conversations"),
          { participantId: friend.id || friend._id },
          { withCredentials: true }
        );
        const conv = convData?.data?.conversation;
        if (!conv?._id) throw new Error("Could not create conversation");
        if (!mounted) return;

        setConversationId(chatId, conv._id);

        const { data: msgData } = await axios.get(
          apiUrl(
            `/api/v1/messages/conversations/${conv._id}/messages?page=1&limit=20`
          ),
          { withCredentials: true }
        );
        if (!mounted) return;
        const msgs = msgData?.data?.messages || [];
        setMessages(msgs.reverse());

        try {
          await axios.patch(
            apiUrl(`/api/v1/messages/conversations/${conv._id}/read`),
            {},
            { withCredentials: true }
          );
        } catch (_) {}
      } catch (e) {
        if (mounted) setError(e?.response?.data?.message || e?.message || "Failed to load chat");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();
    return () => { mounted = false; };
  }, [chatId, friend.id, friend._id, setConversationId]);

  // ─── 2. Auto-scroll on new messages ──────────────────────────────

  useEffect(() => {
    if (!minimized) {
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 0);
    }
  }, [messages.length, minimized]);

  // ─── 3. Focus input when opened / un-minimized ───────────────────

  useEffect(() => {
    if (!minimized && !loading) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [minimized, loading]);

  // ─── 4. Subscribe to incoming messages via miniChatBus ───────────

  useEffect(() => {
    const handler = (newMessage) => {
      const senderId = String(
        newMessage?.sender?._id || newMessage?.sender?.id || newMessage?.sender
      );
      const friendId = String(friend.id || friend._id);

      if (senderId === friendId) {
        setMessages((prev) => [...prev, newMessage]);

        if (conversationId) {
          axios
            .patch(
              apiUrl(`/api/v1/messages/conversations/${conversationId}/read`),
              {},
              { withCredentials: true }
            )
            .catch(() => {});
        }
      }
    };

    miniChatBus.subscribe(chatId, handler);
    return () => miniChatBus.unsubscribe(chatId);
  }, [chatId, friend.id, friend._id, conversationId]);

  // ─── 5. Listen for message status updates ────────────────────────

  useEffect(() => {
    if (!socket) return;

    const handleRead = (data) => {
      if (!data?.messageIds) return;
      const ids = new Set(data.messageIds);
      setMessages((prev) =>
        prev.map((m) => (ids.has(m._id) ? { ...m, status: "read" } : m))
      );
    };

    const handleDelivered = (data) => {
      if (!data?.messageId) return;
      setMessages((prev) =>
        prev.map((m) =>
          m._id === data.messageId && m.status === "sent"
            ? { ...m, status: "delivered" }
            : m
        )
      );
    };

    socket.on("messages_read", handleRead);
    socket.on("message_delivered", handleDelivered);
    return () => {
      socket.off("messages_read", handleRead);
      socket.off("message_delivered", handleDelivered);
    };
  }, []);

  // ─── 6. Send message (text and/or files) ──────────────────────────

  const handleSend = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      const content = draft.trim();
      const hasFiles = selectedFiles.length > 0;
      if ((!content && !hasFiles) || !conversationId || sending) return;

      try {
        setSending(true);
        setUploadProgress(0);

        let data;

        if (hasFiles) {
          const formData = new FormData();
          if (content) formData.append("content", content);
          selectedFiles.forEach((file) => formData.append("attachments", file));

          const resp = await axios.post(
            apiUrl(`/api/v1/messages/conversations/${conversationId}/messages`),
            formData,
            {
              withCredentials: true,
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: (evt) => {
                const pct = Math.round((evt.loaded * 100) / (evt.total || 1));
                setUploadProgress(pct);
              },
            }
          );
          data = resp.data;
        } else {
          const resp = await axios.post(
            apiUrl(`/api/v1/messages/conversations/${conversationId}/messages`),
            { content },
            { withCredentials: true }
          );
          data = resp.data;
        }

        const msg = data?.data?.message;
        if (msg) {
          setMessages((prev) => [...prev, msg]);

          if (socket?.connected) {
            socket.emit("new_message", {
              message: msg,
              chat: {
                users: [
                  { _id: currentUser._id || currentUser.id },
                  { _id: friend.id || friend._id },
                ],
              },
            });
          }
        }
        setDraft("");
        clearFiles();
      } catch (e2) {
        setError(e2?.response?.data?.message || "Failed to send");
        setTimeout(() => setError(""), 3000);
      } finally {
        setSending(false);
        setUploadProgress(0);
      }
    },
    [draft, selectedFiles, conversationId, sending, currentUser, friend, clearFiles]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // ─── 7. Clipboard paste handler ──────────────────────────────────

  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const files = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file") {
          const file = items[i].getAsFile();
          if (file) files.push(file);
        }
      }

      if (files.length > 0) {
        e.preventDefault();
        addFiles(files);
      }
    },
    [addFiles]
  );

  // ─── 8. Drag and drop handlers ───────────────────────────────────

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setDragging(false);

      const files = e.dataTransfer?.files;
      if (files?.length > 0) addFiles(files);
    },
    [addFiles]
  );

  // ─── 9. File picker handler ──────────────────────────────────────

  const handleFileSelect = useCallback(
    (e) => {
      const files = e.target?.files;
      if (files?.length > 0) addFiles(files);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [addFiles]
  );

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <div
      className={`${styles.miniChat} ${minimized ? styles.minimized : ""}`}
      style={{ right: rightPosition }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className={styles.header} onClick={() => toggleMinimize(chatId)}>
        <img
          src={friend.photo || "/images/default_pic.png"}
          alt=""
          className={styles.headerAvatar}
        />
        <span className={styles.headerName}>{friendName}</span>
        <span className={styles.onlineDot} />
        <span className={styles.headerActions}>
          <button
            className={styles.headerBtn}
            onClick={(e) => {
              e.stopPropagation();
              toggleMinimize(chatId);
            }}
            title={minimized ? "Expand" : "Minimize"}
          >
            <i
              className={`fa-solid ${
                minimized ? "fa-chevron-up" : "fa-chevron-down"
              }`}
              style={{ fontSize: 11 }}
            />
          </button>
          <button
            className={styles.headerBtn}
            onClick={(e) => {
              e.stopPropagation();
              closeMiniChat(chatId);
            }}
            title="Close"
          >
            <i className="fa-solid fa-xmark" style={{ fontSize: 13 }} />
          </button>
        </span>
      </div>

      {/* ── Message area ─────────────────────────────────────────── */}
      {!minimized && (
        <>
          <div className={styles.messageArea}>
            {/* Drop zone overlay */}
            {dragging && (
              <div className={styles.dropOverlay}>
                <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: 28 }} />
                <span>Drop files here</span>
              </div>
            )}

            {loading ? (
              <div className={styles.loading}>Loading messages...</div>
            ) : error && messages.length === 0 ? (
              <div className={styles.loading} style={{ color: "#e53935" }}>
                {error}
              </div>
            ) : messages.length === 0 ? (
              <div className={styles.emptyChat}>
                Say hi to {friendName}!
              </div>
            ) : (
              messages.map((m) => {
                const senderId = String(
                  m?.sender?._id || m?.sender?.id || m?.sender
                );
                const isMine =
                  senderId === String(currentUser?._id || currentUser?.id);

                return (
                  <div
                    key={m._id || m.createdAt}
                    className={`${styles.msgRow} ${
                      isMine ? styles.msgRowSent : styles.msgRowReceived
                    }`}
                  >
                    <div
                      className={`${styles.msgBubble} ${
                        isMine ? styles.bubbleSent : styles.bubbleReceived
                      }`}
                    >
                      {/* Attachments */}
                      {Array.isArray(m?.attachments) &&
                        m.attachments.length > 0 && (
                          <div style={{ marginBottom: m?.content ? 4 : 0 }}>
                            {m.attachments.map((att, idx) =>
                              att.type === "image" ? (
                                <a
                                  key={idx}
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <img
                                    src={att.url}
                                    alt={att.name || ""}
                                    style={{
                                      maxWidth: "100%",
                                      borderRadius: 8,
                                      marginBottom: 2,
                                    }}
                                  />
                                </a>
                              ) : att.type === "video" ? (
                                <video
                                  key={idx}
                                  src={att.url}
                                  controls
                                  style={{
                                    maxWidth: "100%",
                                    borderRadius: 8,
                                    marginBottom: 2,
                                  }}
                                />
                              ) : att.type === "audio" ? (
                                <audio
                                  key={idx}
                                  src={att.url}
                                  controls
                                  style={{
                                    width: "100%",
                                    marginBottom: 2,
                                  }}
                                />
                              ) : (
                                <a
                                  key={idx}
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    fontSize: 12,
                                    color: "inherit",
                                    textDecoration: "underline",
                                    marginBottom: 2,
                                  }}
                                >
                                  <i className="fa-solid fa-file" />
                                  <span>{att.name || "Download"}</span>
                                </a>
                              )
                            )}
                          </div>
                        )}

                      {/* Text */}
                      {m?.content && <span>{m.content}</span>}

                      {/* Meta: time + ticks */}
                      {m?.createdAt && (
                        <div
                          className={`${styles.msgMeta} ${
                            isMine
                              ? styles.msgMetaSent
                              : styles.msgMetaReceived
                          }`}
                        >
                          <span>{friendlyTimeShort(m.createdAt)}</span>
                          {isMine && (
                            <span
                              style={{
                                fontSize: 10,
                                marginLeft: 1,
                                color:
                                  m.status === "read"
                                    ? "#53bdeb"
                                    : "inherit",
                                opacity: m.status === "read" ? 1 : 0.7,
                                letterSpacing:
                                  m.status === "sent" ? 0 : -3,
                                fontWeight: 600,
                              }}
                              title={
                                m.status === "read"
                                  ? "Read"
                                  : m.status === "delivered"
                                  ? "Delivered"
                                  : "Sent"
                              }
                            >
                              {m.status === "sent" ? "\u2713" : "\u2713\u2713"}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>

          {/* ── File previews ──────────────────────────────────────── */}
          {filePreviews.length > 0 && (
            <div className={styles.filePreviews}>
              {filePreviews.map((fp, i) => (
                <div key={i} className={styles.filePreviewItem}>
                  {fp.url ? (
                    <img src={fp.url} alt="" className={styles.filePreviewImg} />
                  ) : (
                    <div className={styles.filePreviewGeneric}>
                      <i className={`fa-solid ${fileIcon(fp.type)}`} />
                    </div>
                  )}
                  <button
                    className={styles.filePreviewRemove}
                    onClick={() => removeFile(i)}
                    title="Remove"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── Upload progress ─────────────────────────────────────── */}
          {sending && selectedFiles.length > 0 && (
            <div className={styles.uploadBar}>
              <div
                className={styles.uploadBarFill}
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          {/* ── Input area ───────────────────────────────────────── */}
          <form className={styles.inputArea} onSubmit={handleSend}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <button
              type="button"
              className={styles.attachBtn}
              onClick={() => fileInputRef.current?.click()}
              title="Attach file"
              disabled={!conversationId || loading}
            >
              <i className="fa-solid fa-paperclip" style={{ fontSize: 14 }} />
            </button>
            <input
              ref={inputRef}
              type="text"
              className={styles.inputField}
              placeholder="Type a message"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              disabled={!conversationId || loading}
            />
            <button
              type="submit"
              className={styles.sendBtn}
              disabled={(!draft.trim() && selectedFiles.length === 0) || !conversationId || sending}
              title="Send"
            >
              <i className="fa-solid fa-paper-plane" style={{ fontSize: 13 }} />
            </button>
          </form>

          {/* ── Error toast ──────────────────────────────────────── */}
          {error && messages.length > 0 && (
            <div
              style={{
                position: "absolute",
                bottom: 50,
                left: 10,
                right: 10,
                background: "#e53935",
                color: "#fff",
                padding: "4px 8px",
                borderRadius: 6,
                fontSize: 11,
                textAlign: "center",
              }}
            >
              {error}
            </div>
          )}
        </>
      )}
    </div>
  );
}
